import { Directive, ElementRef, HostListener, Output, ViewContainerRef, EventEmitter } from '@angular/core';
import { ImagesCacheService } from '../services/images-cache.service';
import { PresentationStateService } from '../services/dicom/presentation-state.service';
import { environment } from 'src/environments/environment';
import { AlertService } from '../services/alert.service';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader/dist/dynamic-import/cornerstoneWADOImageLoader.min.js';

declare const cornerstone: any;
declare const cornerstoneTools: any;

@Directive({
  selector: '[CornerstoneLite]'
})
export class CornerstoneLiteDirective {

  public element!: HTMLDivElement;
  public currentImage: any;
  public imageList: { dcm: string | null, png: string | null }[] = [];
  public currentIndex: number = 0;
  private isPlayingClip = false;
  private playingClipInterval: any = null;
  private stackScrollMouseWheelEnabled = false;
  private activeTool: string = 'Wwwc';

  @Output() navigatedSerie = new EventEmitter<'next' | 'prev'>();

  get isPlaying(): boolean {
    return this.isPlayingClip;
  }

  public get windowingValue(): string {
    if (this.isCornerstoneEnabled) {
      let viewport = cornerstone.getViewport(this.element);
      if (this.currentImage && viewport) { return Math.round(viewport.voi.windowWidth) + "/" + Math.round(viewport.voi.windowCenter); }
    }
    return '';
  }

  public get zoomValue(): string {
    if (this.isCornerstoneEnabled) {
      let viewport = cornerstone.getViewport(this.element);
      if (this.currentImage && viewport) { return viewport.scale.toFixed(2); }
    }
    return '';
  }

  private isCornerstoneEnabled = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.isCornerstoneEnabled) {
      cornerstone.resize(this.element, false);
    }
  }

  onMouseWheel(event: MouseEvent) {
    event.preventDefault();
    if (this.isPlayingClip) {
      this.stopClip();
      return;
    }
    if (this.stackScrollMouseWheelEnabled) {
      if (this.imageList.length > 0 && !this.isPlayingClip) {
        const delta = Math.max(-1, Math.min(1, ((event as any).wheelDelta || -event.detail)));
        if (delta > 0) {
          this.navigatedSerie.emit('next')
        } else {
          this.navigatedSerie.emit('prev')
        }
      }
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    if (this.isPlayingClip) {
      this.stopClip();
      return;
    }
  }

  constructor(
    public elementRef: ElementRef,
    public viewContainerRef: ViewContainerRef,
    private presentationStateService: PresentationStateService,
    private alertService: AlertService
  ) {
  }

  ngOnInit() {
    this.refreshElements();
    this.resetViewer();
  }

  public hasNext() {
    if (this.imageList?.length == null) return false
    return this.currentIndex < this.imageList.length - 1;
  }

  public hasPrev() {
    if (this.imageList?.length == null) return false
    return this.currentIndex > 0;
  }

  setImages(imageList: { dcm: string | null, png: string | null }[]) {
    this.imageList = imageList;

    if (imageList == null || imageList.length == 0) return;

    if (imageList.length == 1) {
      this.currentIndex = 0;
      this.showImage(this.imageList[0].dcm!);
      this.activateZoomWithMouseWheel();
    } else {
      this.currentIndex = Math.floor(this.imageList.length / 2);
      this.showImage(this.imageList[this.currentIndex].dcm!);
      this.activateScrollWithMouseWheel();
    }
    this.activateTool('Pan', 2);
    this.activateTool('Wwwc', 1);
    document.dispatchEvent(new Event('dicomViewer:changedSerie'))
  }

  showImage(imageId: string) {
    
    // Comprobamos si la imagen en cuestión ya ha sido cacheada
    const cached = cornerstone.metaData.get('imagePlaneModule', imageId) != null;

    if (cached) {
      cornerstone.loadAndCacheImage(imageId).then((imageData: any) => {
        this.currentImage = imageData;
        this.element.dispatchEvent(new Event('changedImage'));
        this.refreshData();
        this.activateTool('Wwwc')
      }).catch((err: any) => {
        console.error(err);
        this.alertService.alert('No se ha podido abrir la imagen. Por favor, póngase en contacto con su centro');
      });
    } else {
      this.currentImage = null;
      // La imagen de verdad la cargamos si el usuario lleva más de 250ms en la misma imagen
      setTimeout(() => {
        if (this.currentIndex == this.imageList.findIndex(i => i.dcm == imageId)) {
          // cornerstone.loadAndCacheImage('wadouri:http://localhost:8743/image/getImage?studyUID=a&seriesUID=a&objectUID=a&requestType=wado').then((imageData) => {
          cornerstone.loadAndCacheImage(imageId).then((imageData: any) => {
            // Comprobamos que seguimos en la misma imagen antes de ponerla en this.currentImage
            if (this.currentIndex == this.imageList.findIndex(i => i.dcm == imageId)) {
              this.currentImage = imageData;
              this.element.dispatchEvent(new Event('changedImage'));
              this.refreshData();
              this.activateTool('Wwwc')
            }
          }).catch((err: any) => {
            console.error(err);
            this.alertService.alert('No se ha podido abrir la imagen. Por favor, póngase en contacto con su centro');
          });
        }
      }, (environment.demo) ? 0 : 250);
    }
    if (environment.demo) {
      const index = this.imageList.findIndex(i => i.dcm == imageId);
      try {
        cornerstone.loadAndCacheImage(this.imageList[index - 1].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index + 1].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index - 2].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index + 2].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index - 3].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index + 3].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index - 4].dcm)
      } catch { }
      try {
        cornerstone.loadAndCacheImage(this.imageList[index + 4].dcm)
      } catch { }
    }
  }

  public nextImage() {
    if (this.isPlaying) {
      this.stopClip();
    }
    this.currentIndex++;

    if (this.currentIndex >= (this.imageList.length)) {
      this.currentIndex = this.imageList.length - 1;
    }
    this.showImage(this.imageList[this.currentIndex].dcm!);
    document.dispatchEvent(new Event('dicomViewer:scrolledImage'))
  }

  public prevImage() {
    if (this.isPlaying) {
      this.stopClip();
    }
    this.currentIndex--;
    if (this.currentIndex < 0) {
      this.currentIndex = 0;
    }
    this.showImage(this.imageList[this.currentIndex].dcm!);
    document.dispatchEvent(new Event('dicomViewer:scrolledImage'))
  }

  public jumpToIndex(index: number) {
    if (index == this.currentIndex) return;
    if (this.isPlayingClip) {
      this.stopClip();
    }
    if (index == null) {
      this.currentIndex = 0;
    } else {
      if (index >= (this.imageList.length)) {
        this.currentIndex = this.imageList.length - 1;
      } else if (index < 0) {
        this.currentIndex = 0;
      } else {
        this.currentIndex = index;
      }
    }
    this.showImage(this.imageList[this.currentIndex].dcm!);
  }

  public activateTool(toolName: string, btn = 1, synchronizer?: any) {
    this.activeTool = toolName;
    if (synchronizer != null) {
      cornerstoneTools.setToolActiveForElement(this.element, toolName, { mouseButtonMask: btn, synchronizationContext: synchronizer }, ['Mouse', 'Touch']);
    } else {
      cornerstoneTools.setToolActiveForElement(this.element, toolName, { mouseButtonMask: btn }, ['Mouse', 'Touch']);
    }
  }

  public reset() {
    if (this.isPlayingClip) {
      this.stopClip();
    }
    cornerstone.reset(this.element);
  }

  private disableViewer() {
    if (this.isPlayingClip) {
      this.stopClip();
    }
    this.refreshElements();
    try {
      cornerstone.disable(this.element);
    } finally { }

    this.isCornerstoneEnabled = false;
  }

  public resetViewer() {
    if (this.isPlayingClip) {
      this.stopClip();
    }
    this.disableViewer();
    cornerstone.enable(this.element);
    this.isCornerstoneEnabled = true;
  }

  onMeasurementCompletedListener: EventListenerOrEventListenerObject = (evt) => this.onMeasurementCompleted();
  onStartMeasurementListener: EventListenerOrEventListenerObject = (evt) => this.onStartMeasurement();
  onCancelMeasurementListener: EventListenerOrEventListenerObject = (evt) => this.onCancelMeasurement(evt);
  private refreshElements() {
    // Retrieve the DOM element itself
    // if (this.isPlayingClip) {
    //   this.stopClip();
    // }
    this.element = this.elementRef.nativeElement;
    try {
      cornerstone.getEnabledElement(this.element);
    } catch (error) {
      cornerstone.enable(this.element);
      this.isCornerstoneEnabled = true;
    }
    this.element.removeEventListener(cornerstoneTools.EVENTS.MEASUREMENT_COMPLETED, this.onMeasurementCompletedListener);
    this.element.removeEventListener(cornerstoneTools.EVENTS.MEASUREMENT_ADDED, this.onStartMeasurementListener);
    this.element.removeEventListener('mouseleave', this.onCancelMeasurementListener);
    document.removeEventListener('keyup', this.onCancelMeasurementListener);

    this.element.addEventListener(cornerstoneTools.EVENTS.MEASUREMENT_COMPLETED, this.onMeasurementCompletedListener);
    this.element.addEventListener(cornerstoneTools.EVENTS.MEASUREMENT_ADDED, this.onStartMeasurementListener);
    this.element.addEventListener('mouseleave', this.onCancelMeasurementListener);
    document.addEventListener('keyup', this.onCancelMeasurementListener);
  }

  public resetCache() {
    if (this.isPlayingClip) {
      this.stopClip();
    }
    this.element.dispatchEvent(new Event('changedImage'));
    this.currentImage = null;
    this.currentIndex = 0;
    this.imageList = [];
  }

  activateZoomWithMouseWheel() {
    this.stackScrollMouseWheelEnabled = false;
    // cornerstoneTools.setToolDisabled(this.element, 'StackScrollMouseWheelTool', {});
    cornerstoneTools.setToolActive('ZoomMouseWheel', { mouseButtonMask: 1 })
  }

  activateScrollWithMouseWheel() {
    this.stackScrollMouseWheelEnabled = true;
    cornerstoneTools.setToolDisabled('ZoomMouseWheel', { mouseButtonMask: 1 })
    // cornerstoneTools.setToolActiveForElement(this.element, 'StackScrollMouseWheelTool', {});
  }

  refreshData() {
    this.refreshElements();
    if (this.currentImage == null) {
      return;
    }

    let vp = cornerstone.getDefaultViewportForImage(this.element, this.currentImage);
    cornerstone.displayImage(this.element, this.currentImage, vp);
    window.dispatchEvent(new Event('resize'));
    // Extraemos los datos de las herramientas
    const toolsData = this.presentationStateService.getToolDataForInstanceUID(this.currentImage.data.string('x00080018'));
    if (toolsData) {
      Object.keys(toolsData).forEach(d => {
        if (isNaN(+d)) {
          const toolData = toolsData[d].data;
          toolData.forEach((data: any) => {
            cornerstoneTools.addToolState(this.element, d, data);
          });
          this.activateTool('ExternalAnnotations')
          this.activateTool('Wwwc')
        }
      });
    }
  }

  playClip(interval = 100) {
    // if (framerate == null) {
    //   framerate = 5;
    // }
    // cornerstoneTools.playClip(this.element, framerate);
    if (!this.isPlayingClip) {
      this.playingClipInterval = setInterval(() => {
        if (this.imageList.length > 0) {
          this.currentIndex++;
          if (this.currentIndex >= (this.imageList.length)) {
            this.currentIndex = 0
          }
          document.dispatchEvent(new Event('dicomViewer:scrolledImage'))
        }
        this.showImage(this.imageList[this.currentIndex].dcm!);
        // this.refreshData();
      }, interval);
      this.isPlayingClip = true;
    }
  }

  stopClip() {
    // cornerstoneTools.stopClip(this.element);
    clearInterval(this.playingClipInterval);
    this.isPlayingClip = false;
    // const state = cornerstoneTools.getToolState(this.element, 'stack');
    // this.jumpToIndex(+state.data[0].currentImageIdIndex)
  }

  invert() {
    const viewport = cornerstone.getViewport(this.element);
    viewport.invert = !viewport.invert;
    cornerstone.setViewport(this.element, viewport);
  }

  hflip() {
    const viewport = cornerstone.getViewport(this.element);
    viewport.hflip = !viewport.hflip;
    cornerstone.setViewport(this.element, viewport);
  }

  vflip() {
    const viewport = cornerstone.getViewport(this.element);
    viewport.vflip = !viewport.vflip;
    cornerstone.setViewport(this.element, viewport);
  }

  rotate(deg: number) {
    const viewport = cornerstone.getViewport(this.element);
    viewport.rotation = viewport.rotation + deg;
    cornerstone.setViewport(this.element, viewport);
  }

  // deactivate all tools
  public resetAllTools() {
    cornerstoneTools.setToolDisabledForElement(this.element, 'Wwwc');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Pan');
    cornerstoneTools.setToolDisabledForElement(this.element, 'Zoom');
  }

  /** Cancelación de medidas */
  isMeasuring = false;

  onMeasurementCompletedTimeout = null;
  onMeasurementCompleted() {
    this.isMeasuring = false;
  }

  onStartMeasurementTimeout = null;
  onStartMeasurement() {
    this.isMeasuring = true;
  }

  onCancelMeasurementTimeout: any = null;
  onCancelMeasurement(event: any) {
    if (event.key == "Escape" || event.type == 'mouseleave') {
      if (this.onCancelMeasurementTimeout == null) {
        this.onCancelMeasurementTimeout = setTimeout(() => {
          this.onCancelMeasurementTimeout = null;
          this.cancelMeasurement();
        }, 20);
      }
    }
  }

  cancelMeasurement() {
    if (this.isMeasuring) {
      (window as any).element = this.element;
      let toolState = cornerstoneTools.getToolState(this.element, this.activeTool);
      if (toolState != null) {
        toolState.data.pop();
        cornerstone.updateImage(this.element);
        this.isMeasuring = false;
      }
    }
  }

  /* ----------------------- */

}
